import React, { useState, useEffect } from 'react';
import { Search, Calendar, MapPin, Navigation, Trophy, TrendingUp, Activity, ChevronRight, X, Sparkles, Zap, RefreshCw } from 'lucide-react';
import axios from 'axios';

interface WeeklySummary {
    user_id: number;
    username: string;
    full_name?: string;
    avatar_url?: string;
    total_distance_km: number;
    total_trips: number;
    location_count: number;
}

interface WeeklyStats {
    active_users: number;
    total_trips: number;
    total_locations: number;
    period: { start: string; end: string };
}

interface UserDetail {
    user: { id: number; username: string; full_name?: string; avatar_url?: string };
    summary: { total_distance_km: number; total_trips: number; location_count: number; most_active_day: string; most_active_distance: number };
    daily_distances: { [key: string]: number };
    trips: { id: number; destination?: string; distance: number; status: string; start_time?: string }[];
}

const DAY_LABELS: { [key: string]: string } = { Mon: 'Pzt', Tue: 'Sal', Wed: 'Çar', Thu: 'Per', Fri: 'Cum', Sat: 'Cmt', Sun: 'Paz' };

const WeeklySummaryManagement: React.FC = () => {
    const [summaries, setSummaries] = useState<WeeklySummary[]>([]);
    const [stats, setStats] = useState<WeeklyStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [storyIndex, setStoryIndex] = useState(0);

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        setRefreshing(true);
        try {
            const [summariesRes, statsRes] = await Promise.all([
                axios.get('https://api.lifeshare.app/api/v1/admin/weekly-summaries'),
                axios.get('https://api.lifeshare.app/api/v1/admin/weekly-summaries/stats')
            ]);
            setSummaries(summariesRes.data.summaries || []);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const fetchUserDetail = async (userId: number) => {
        setDetailLoading(true);
        setStoryIndex(0);
        try {
            const response = await axios.get(`https://api.lifeshare.app/api/v1/admin/weekly-summaries/${userId}`);
            setSelectedUser(response.data);
        } catch (error) {
            console.error('User detail error:', error);
        } finally {
            setDetailLoading(false);
        }
    };

    const filteredSummaries = summaries.filter(s =>
        (s.full_name || '').toLowerCase().includes(searchText.toLowerCase()) ||
        s.username.toLowerCase().includes(searchText.toLowerCase())
    );

    const getDistanceColor = (distance: number) => {
        if (distance >= 100) return 'from-purple-500 to-indigo-600';
        if (distance >= 50) return 'from-blue-500 to-cyan-600';
        if (distance >= 20) return 'from-green-500 to-emerald-600';
        return 'from-gray-400 to-gray-500';
    };

    const getRankBadge = (index: number) => {
        if (index === 0) return '🥇';
        if (index === 1) return '🥈';
        if (index === 2) return '🥉';
        return null;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="max-w-5xl mx-auto space-y-6">

                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Haftalık Özetler</h1>
                        <p className="text-sm text-gray-500 mt-1">Spotify Wrapped tarzı kullanıcı istatistikleri</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {stats && (
                            <span className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-xl text-xs font-medium">
                                <Calendar className="w-3 h-3 inline mr-1" />{stats.period.start} - {stats.period.end}
                            </span>
                        )}
                        <button onClick={fetchData} disabled={refreshing} className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors">
                            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-3 gap-3">
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Activity className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-purple-700">{stats.active_users}</p>
                            <p className="text-xs text-purple-600">Aktif Kullanıcı</p>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Navigation className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-blue-700">{stats.total_trips}</p>
                            <p className="text-xs text-blue-600">Toplam Trip</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><MapPin className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-green-700">{stats.total_locations.toLocaleString()}</p>
                            <p className="text-xs text-green-600">Konum Kaydı</p>
                        </div>
                    </div>
                )}

                {/* Search */}
                <div className="bg-white rounded-2xl p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type="text" placeholder="Kullanıcı ara..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500" />
                    </div>
                </div>

                {/* Leaderboard */}
                <div className="bg-white rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-4">
                        <Trophy className="w-5 h-5 text-yellow-500" />
                        <h2 className="font-semibold text-gray-900">En Aktif Kullanıcılar</h2>
                    </div>

                    {filteredSummaries.length === 0 ? (
                        <div className="text-center py-8">
                            <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                            <p className="text-gray-500">Arama sonucu bulunamadı</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {filteredSummaries.map((summary, index) => (
                                <div key={summary.user_id} onClick={() => fetchUserDetail(summary.user_id)} className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-xl cursor-pointer transition-all group">
                                    <div className="w-8 text-center">
                                        {getRankBadge(index) ? <span className="text-lg">{getRankBadge(index)}</span> : <span className="text-sm font-bold text-gray-400">#{index + 1}</span>}
                                    </div>
                                    {summary.avatar_url ? (
                                        <img src={`https://api.lifeshare.app${summary.avatar_url}`} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                                    ) : (
                                        <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getDistanceColor(summary.total_distance_km)} flex items-center justify-center text-white font-bold flex-shrink-0`}>
                                            {(summary.full_name || summary.username).charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="font-semibold text-gray-900 text-sm truncate">{summary.full_name || summary.username}</p>
                                        <p className="text-xs text-gray-500">@{summary.username}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-bold text-gray-900">{summary.total_distance_km} km</p>
                                        <p className="text-xs text-gray-500">{summary.total_trips} trip</p>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* iOS Story Style Detail Modal */}
                {selectedUser && (
                    <div className="fixed inset-0 bg-black z-50 flex items-center justify-center" onClick={() => setSelectedUser(null)}>
                        <div className="absolute top-4 left-4 right-4 flex gap-1 z-10">
                            {[0, 1, 2].map(i => (
                                <div key={i} className="flex-1 h-1 rounded-full bg-white/30 overflow-hidden">
                                    <div className={`h-full bg-white transition-all duration-300 ${i < storyIndex ? 'w-full' : i === storyIndex ? 'w-full animate-pulse' : 'w-0'}`} />
                                </div>
                            ))}
                        </div>
                        <button onClick={() => setSelectedUser(null)} className="absolute top-10 right-4 p-2 bg-white/20 rounded-full z-10 hover:bg-white/30"><X className="w-5 h-5 text-white" /></button>
                        <div className="absolute inset-0 flex" onClick={e => e.stopPropagation()}>
                            <div className="w-1/3 h-full" onClick={() => setStoryIndex(prev => Math.max(prev - 1, 0))} />
                            <div className="w-1/3 h-full" />
                            <div className="w-1/3 h-full" onClick={() => setStoryIndex(prev => Math.min(prev + 1, 2))} />
                        </div>

                        {detailLoading ? (
                            <div className="text-white text-center"><div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto" /></div>
                        ) : (
                            <div className="w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>
                                {storyIndex === 0 && (
                                    <div className="text-center text-white animate-fade-in">
                                        <div className="mb-8"><Sparkles className="w-8 h-8 mx-auto mb-2 text-yellow-400" /><p className="text-sm text-white/60 uppercase tracking-widest">Haftalık Özet</p></div>
                                        {selectedUser.user.avatar_url ? (
                                            <img src={`https://api.lifeshare.app${selectedUser.user.avatar_url}`} alt="" className="w-32 h-32 rounded-full border-4 border-white/30 mx-auto mb-6 object-cover" />
                                        ) : (
                                            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-5xl font-bold mx-auto mb-6">{(selectedUser.user.full_name || selectedUser.user.username).charAt(0)}</div>
                                        )}
                                        <h2 className="text-3xl font-bold mb-2">{selectedUser.user.full_name || selectedUser.user.username}</h2>
                                        <p className="text-white/60">@{selectedUser.user.username}</p>
                                        <div className="mt-8 text-sm text-white/40">Dokunarak devam et →</div>
                                    </div>
                                )}
                                {storyIndex === 1 && (
                                    <div className="text-center text-white animate-fade-in">
                                        <div className="mb-8"><Zap className="w-8 h-8 mx-auto mb-2 text-yellow-400" /><p className="text-sm text-white/60 uppercase tracking-widest">Bu Hafta</p></div>
                                        <div className="space-y-6">
                                            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6">
                                                <p className="text-6xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">{selectedUser.summary.total_distance_km}</p>
                                                <p className="text-white/60 mt-2">kilometre yol kat ettin! 🚗</p>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4"><p className="text-3xl font-bold">{selectedUser.summary.total_trips}</p><p className="text-xs text-white/60">yolculuk</p></div>
                                                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4"><p className="text-3xl font-bold">{selectedUser.summary.location_count}</p><p className="text-xs text-white/60">konum kaydı</p></div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {storyIndex === 2 && (
                                    <div className="text-center text-white animate-fade-in">
                                        <div className="mb-8"><TrendingUp className="w-8 h-8 mx-auto mb-2 text-green-400" /><p className="text-sm text-white/60 uppercase tracking-widest">Günlük Aktivite</p></div>
                                        <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6">
                                            <div className="flex items-end justify-between gap-2 h-32 mb-4">
                                                {Object.entries(selectedUser.daily_distances).map(([day, dist]) => {
                                                    const maxDist = Math.max(...Object.values(selectedUser.daily_distances), 1);
                                                    const height = (dist / maxDist) * 100;
                                                    const isActive = day === selectedUser.summary.most_active_day;
                                                    return (
                                                        <div key={day} className="flex-1 flex flex-col items-center gap-2">
                                                            <div className={`w-full rounded-t-lg transition-all ${isActive ? 'bg-gradient-to-t from-green-500 to-green-400' : 'bg-white/30'}`} style={{ height: `${Math.max(height, 8)}%` }} />
                                                            <span className={`text-xs ${isActive ? 'text-green-400 font-bold' : 'text-white/60'}`}>{DAY_LABELS[day]}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <p className="text-sm text-white/60">En aktif gün: <span className="text-white font-bold">{DAY_LABELS[selectedUser.summary.most_active_day]}</span></p>
                                        </div>
                                        {selectedUser.trips.length > 0 && (
                                            <div className="mt-6 text-left">
                                                <p className="text-xs text-white/40 mb-2">Son Yolculuklar</p>
                                                <div className="space-y-2">
                                                    {selectedUser.trips.slice(0, 3).map(trip => (
                                                        <div key={trip.id} className="flex items-center gap-3 bg-white/10 rounded-xl p-3">
                                                            <Navigation className="w-4 h-4 text-blue-400" />
                                                            <span className="text-sm truncate flex-1">{trip.destination}</span>
                                                            <span className="text-xs text-white/60">{trip.distance} km</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default WeeklySummaryManagement;
